import React, { useState, useEffect } from 'react';
import {
  DeleteIcon,
  DragIcon,
  InformationIcon,
  MediaIcon,
  Pencil,
  DirectoryIcon,
  CommentIcon,
} from 'src/images';
import styles from './checklist-item.module.scss';
import classnames from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { Checkbox } from 'antd';
import { Lang } from 'shared/constants';
import { MrkdwnEditor } from 'src/components/core';
import { IconButton } from 'src/components/design-system/buttons';

const ChecklistItemComponent = React.memo(function ChecklistItemComponent({
  item = {
    title: '',
    value: '',
  },
  categoryId = '',
  light = null,
  adminMode = false,
  deleteValue = () => {},
  editValue = () => {},
  moveAction = () => {},
  dragItem = () => {},
  findItem = () => {},
  dndGroup = '',
  draggable = true,
  finishDrag = () => {},
  checkedState = false,
  admissionDetails,
  addCommentCheck = () => {},
  updateChecklistState = () => {},
  lastUpdated = '',
  updateCheck = () => {},
  lang = Lang.CHECKLIST_ITEM_EDITOR,
  langTooltip = Lang.ACTION_BUTTON_TOOLTIPS,
  canEdit = true,
}) {
  let { id, title, link, info } = item;

  let boxRef = React.useRef(null);

  const [titleInput, setTitleInput] = useState(title);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (titleInput !== title) {
      setTitleInput(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, setTitleInput]);

  const originalIndex = findItem(id)?.index;

  const [{ isDragging }, drag] = useDrag({
    item: { type: dndGroup, id, originalIndex },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => adminMode && draggable,
    end: (dropResult, monitor) => {
      const { id: droppedId, originalIndex } = monitor.getItem();
      const didDrop = monitor.didDrop();
      if (!didDrop) {
        dragItem(droppedId, originalIndex);
      }

      finishDrag(droppedId);
    },
  });

  const [, drop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId }) {
      if (draggedId !== id) {
        const { index: overIndex } = findItem(id);
        dragItem(draggedId, overIndex);
      }
    },
  });

  if (draggable) {
    boxRef = drag(drop(boxRef));
  }

  const onClick = () => {
    if (adminMode) {
      // TODO: analytics
      editValue({ item });
      return;
    }
  };

  const onCheckChange = e => {
    let { checked } = e.target;
    updateChecklistState({
      id: item.id,
      categoryId,
      checked,
    });
  };

  const openLink = React.useCallback(
    e => {
      e.stopPropagation();

      // TODO: analytics
      window.open(link);
    },
    [link],
  );

  const openInformationInner = React.useCallback(
    e => {
      e.stopPropagation();

      // TODO: analytics
      admissionDetails(item);
    },
    [admissionDetails, item],
  );

  const changeDirectory = React.useCallback(
    e => {
      e.stopPropagation();

      moveAction({ id, title });
    },
    [id, moveAction, title],
  );

  const deleteValueItem = React.useCallback(
    e => {
      e.stopPropagation();

      deleteValue({ id, title });
    },
    [deleteValue, id, title],
  );

  const addCommentCheckAction = React.useCallback(
    e => {
      e.stopPropagation();
      addCommentCheck({ id, title });
    },
    [addCommentCheck, id, title],
  );

  const onFocus = React.useCallback(() => {
    setDisabled(true);
  }, [setDisabled]);

  const onChangeValue = async (name, value) => {
    setTitleInput(value);

    if (value === item[name]) {
      setDisabled(false);
      return false;
    }

    await updateCheck({ checkId: id, categoryId, item: { title: value } });
    setDisabled(false);
  };

  const renderActions = () => {
    if (!adminMode) {
      return (
        <div className={styles.buttons}>
          <IconButton
            onClick={addCommentCheckAction}
            className={styles.buttonWrapper}
            label={langTooltip.ADD_COMMENT}
          >
            <CommentIcon className={styles.commentIcon} />
          </IconButton>
          {info && (
            <IconButton
              onClick={openInformationInner}
              className={styles.buttonWrapper}
              label={langTooltip.INFO}
            >
              <InformationIcon className={styles.informationIcon} />
            </IconButton>
          )}
          {link && (
            <IconButton
              onClick={openLink}
              className={styles.buttonWrapper}
              label={langTooltip.OPEN_ATTACHEMENT}
            >
              <MediaIcon className={styles.informationIcon} />
            </IconButton>
          )}
        </div>
      );
    }

    return (
      <div className={styles.buttons}>
        <IconButton
          label={canEdit ? langTooltip.DELETE : langTooltip.NOT_ALLOWED_TO_EDIT}
          onClick={deleteValueItem}
          className={styles.buttonWrapper}
          disabled={!canEdit}
        >
          <DeleteIcon className={styles.deleteIcon} />
        </IconButton>
        <IconButton
          label={canEdit ? langTooltip.EDIT : langTooltip.NOT_ALLOWED_TO_EDIT}
          onClick={onClick}
          className={styles.buttonWrapper}
          disabled={!canEdit}
        >
          <Pencil className={styles.editIcon} />
        </IconButton>

        {!!moveAction && (
          <IconButton
            onClick={changeDirectory}
            className={styles.buttonWrapper}
            label={langTooltip.MOVE}
          >
            <DirectoryIcon className={styles.changeDirectory} />
          </IconButton>
        )}
        {draggable && (
          <IconButton
            label={langTooltip.DRAG}
            className={classnames(styles.buttonWrapper, styles.dragIconWrapper)}
            tabIndex={-1}
            aria-hidden="true"
          >
            <DragIcon className={styles.dragIcon} />
          </IconButton>
        )}
      </div>
    );
  };

  let opacity = isDragging ? 0 : 1;

  return (
    <li
      ref={boxRef}
      style={{ opacity }}
      className={classnames(styles.root, light ? styles.light : {})}
      aria-label={titleInput}
    >
      <div className={styles.content}>
        <Checkbox
          key={lastUpdated}
          className={styles.check}
          defaultChecked={checkedState}
          onChange={onCheckChange}
        />
        <MrkdwnEditor
          className={styles.title}
          wrapperClass={styles.value}
          readOnly={!adminMode || !canEdit}
          value={titleInput}
          onChange={onChangeValue}
          placeholder={lang.ITEM_TITLE_PLACEHOLDER}
          hideControllers={!disabled}
          name="value"
          inputStyle="inline"
          onFocus={onFocus}
        />
      </div>

      {renderActions()}
    </li>
  );
});

export { ChecklistItemComponent };
